import { emailRegex, indianMobileRegex, linkedInRegex } from '../../utils/utils';

/**
 * Validators for the five editable director fields, returning an error message or `undefined`.
 *
 * Every one of them accepts an empty value: clearing a field is a legitimate edit that the
 * server accepts and records, so only a non-empty malformed value is an error.
 */

export const ADDRESS_MAX_LENGTH = 500;
export const OVERVIEW_MAX_LENGTH = 5000;

export const validateIndianMobile = (value: string): string | undefined => {
    const trimmed = value?.trim();
    if (!trimmed) return undefined;
    return indianMobileRegex.test(trimmed) ? undefined : 'Please enter a valid 10-digit mobile number.';
};

export const validateEmailField = (value: string): string | undefined => {
    const trimmed = value?.trim();
    if (!trimmed) return undefined;
    return emailRegex.test(trimmed) ? undefined : 'Please enter a valid email address.';
};

export const validateLinkedInUrlField = (value: string): string | undefined => {
    const trimmed = value?.trim();
    if (!trimmed) return undefined;
    return linkedInRegex.test(trimmed) ? undefined : 'Please enter a valid LinkedIn profile URL.';
};

export const validateAddress = (value: string): string | undefined => {
    const trimmed = value?.trim();
    if (!trimmed) return undefined;
    return trimmed.length <= ADDRESS_MAX_LENGTH ? undefined : `Address cannot exceed ${ADDRESS_MAX_LENGTH} characters.`;
};

export const validateOverview = (value: string): string | undefined => {
    const trimmed = value?.trim();
    if (!trimmed) return undefined;
    return trimmed.length <= OVERVIEW_MAX_LENGTH ? undefined : `Overview cannot exceed ${OVERVIEW_MAX_LENGTH} characters.`;
};

// Keyed by row/column field so both the table cell and the drawer modal resolve the same rule.
const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const DIN_PATTERN = /^[0-9]{8}$/;

const validatePan = (value: string): string | undefined => {
    const trimmed = value.trim().toUpperCase();
    if (!trimmed) return undefined;
    return PAN_PATTERN.test(trimmed) ? undefined : 'Please enter a valid PAN (e.g. ABCDE1234F).';
};

const validateDin = (value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    return DIN_PATTERN.test(trimmed) ? undefined : 'Please enter a valid 8-digit DIN.';
};

export const MANAGEMENT_PROFILE_FIELD_VALIDATORS: Record<string, (value: string) => string | undefined> = {
    pan: validatePan,
    din: validateDin,
    phone: validateIndianMobile,
    email: validateEmailField,
    linkedinUrl: validateLinkedInUrlField,
    address: validateAddress,
    managementProfileSummary: validateOverview,
};
